import { useState } from 'react';
import type { ReactNode } from 'react';
import { useTags } from '../api/queries';
import ui from './ui.module.css';
import styles from './TagPicker.module.css';

/**
 * The poll's categories (02-requirements.md §Poll Categories). A poll carries
 * many; the administrator picks from what already exists or writes a new one,
 * and those are the same gesture — the API creates a tag on first use, so this
 * screen has no "manage categories" mode and cannot leave a category behind
 * with no poll on it.
 *
 * Ordering is meaningful: the **first** tag is the poll's primary category, the
 * one the app prints in `categoria • N votos`, which is why a selected tag can
 * be promoted to the front and why the list is not sorted alphabetically.
 */
export function TagPicker({
  value,
  onChange,
  error,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  /** Message from the caller — removing the last category, or a failed write. */
  error?: string | null;
}): ReactNode {
  const { data: available } = useTags();
  const [typed, setTyped] = useState('');

  const has = (name: string): boolean =>
    value.some((entry) => entry.trim().toLowerCase() === name.trim().toLowerCase());

  const add = (name: string): void => {
    const clean = name.trim().replace(/\s+/g, ' ');
    if (clean.length < 2 || has(clean)) return;
    onChange([...value, clean]);
    setTyped('');
  };

  const remove = (name: string): void => onChange(value.filter((entry) => entry !== name));

  const promote = (name: string): void => onChange([name, ...value.filter((e) => e !== name)]);

  const unused = (available ?? []).filter((tag) => !has(tag.name));

  return (
    <div className={ui.field}>
      <span className={ui.label}>
        Categorias
        <span aria-hidden="true" className={styles.required}>
          *
        </span>
      </span>

      <ul className={styles.chips} aria-label="Categorias da enquete">
        {value.map((name, index) => (
          <li key={name} className={index === 0 ? `${styles.chip} ${styles.primary}` : styles.chip}>
            {index === 0 ? (
              <span className={styles.chipMark} title="Categoria principal">
                ★
              </span>
            ) : (
              <button
                type="button"
                className={styles.chipAction}
                aria-label={`Tornar ${name} a categoria principal`}
                onClick={() => promote(name)}
              >
                ☆
              </button>
            )}
            <span>{name}</span>
            <button
              type="button"
              className={styles.chipAction}
              aria-label={`Remover ${name}`}
              onClick={() => remove(name)}
            >
              ×
            </button>
          </li>
        ))}
        {value.length === 0 ? <li className={ui.muted}>Nenhuma categoria escolhida.</li> : null}
      </ul>

      {error ? <p className={ui.error}>{error}</p> : null}

      <div className={styles.addRow}>
        <input
          className={ui.input}
          aria-label="Nova categoria"
          placeholder="Nova categoria"
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
          onKeyDown={(event) => {
            // Enter adds the tag; without this it would submit nothing and look
            // broken, since the screen has no form to submit.
            if (event.key !== 'Enter') return;
            event.preventDefault();
            add(typed);
          }}
        />
        <button
          type="button"
          className={`${ui.ghost} ${ui.small}`}
          disabled={typed.trim().length < 2}
          onClick={() => add(typed)}
        >
          Adicionar categoria
        </button>
      </div>

      {unused.length > 0 ? (
        <ul className={styles.suggestions} aria-label="Categorias existentes">
          {unused.map((tag) => (
            <li key={tag.id}>
              <button
                type="button"
                className={styles.suggestion}
                onClick={() => add(tag.name)}
                aria-label={`Adicionar categoria ${tag.name}`}
              >
                {tag.name} <span className={styles.count}>{tag.poll_count}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
